import { Alert, Form, Input, InputNumber, Modal, Switch } from 'antd';
import { useEffect } from 'react';
import type { PricingRule } from '@/types';
import {
  mergePricingRuleDraft,
  type PricingRuleFormValues,
} from '../pricingRuleDraft';

interface Props {
  open: boolean;
  rule: PricingRule | null;
  allowIDEdit?: boolean;
  onClose: () => void;
  onSave: (rule: PricingRule) => void;
}

export function PricingRuleFormDialog({
  open,
  rule,
  allowIDEdit = false,
  onClose,
  onSave,
}: Props) {
  const [form] = Form.useForm<PricingRuleFormValues>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        id: rule?.id || '',
        enabled: rule?.enabled ?? true,
        provider: rule?.provider,
        provider_path: rule?.provider_path,
        public_model: rule?.public_model,
        upstream_model: rule?.upstream_model,
        route_id: rule?.route_id,
        models: (rule?.models || []).join(', '),
        input_usd_per_million_tokens: rule?.input_usd_per_million_tokens,
        output_usd_per_million_tokens: rule?.output_usd_per_million_tokens,
        cache_read_usd_per_million_tokens:
          rule?.cache_read_usd_per_million_tokens,
        cache_write_usd_per_million_tokens:
          rule?.cache_write_usd_per_million_tokens,
        image_usd_per_million_tokens: rule?.image_usd_per_million_tokens,
        audio_usd_per_million_tokens: rule?.audio_usd_per_million_tokens,
        fixed_price_usd: rule?.fixed_price_usd,
        price_multiplier: rule?.price_multiplier ?? 1,
        priority: rule?.priority,
        free: rule?.free,
      });
    }
  }, [form, open, rule]);

  const handleOk = async () => {
    const values = await form.validateFields();
    onSave(mergePricingRuleDraft(rule, values));
  };

  return (
    <Modal
      open={open}
      title={allowIDEdit ? '新增/复制价格规则' : '编辑价格规则'}
      onCancel={onClose}
      onOk={handleOk}
      destroyOnClose
      width={560}
    >
      <Form form={form} layout="vertical">
        {rule?.pricing_versions?.length ? (
          <Alert
            className="mb-4"
            type="info"
            showIcon
            message={`该规则包含 ${rule.pricing_versions.length} 个动态价格版本`}
            description="当前表单编辑的是生效前的基础/回退价格；动态版本和档位会原样保留，可在价格表的“动态定价”列查看。"
          />
        ) : null}
        <Form.Item
          label="规则ID"
          name="id"
          rules={[{ required: true, message: '请输入规则 ID' }]}
        >
          <Input disabled={!allowIDEdit} />
        </Form.Item>
        <Form.Item label="启用规则" name="enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
        <div className="grid grid-cols-2 gap-3">
          <Form.Item label="Provider" name="provider">
            <Input />
          </Form.Item>
          <Form.Item label="路径" name="provider_path">
            <Input />
          </Form.Item>
          <Form.Item label="公共模型" name="public_model">
            <Input />
          </Form.Item>
          <Form.Item label="上游模型" name="upstream_model">
            <Input />
          </Form.Item>
          <Form.Item label="Route ID" name="route_id">
            <Input />
          </Form.Item>
          <Form.Item label="Models（逗号分隔）" name="models">
            <Input />
          </Form.Item>
          <Form.Item
            label="输入价 ($/M tokens)"
            name="input_usd_per_million_tokens"
          >
            <InputNumber min={0} step={0.001} className="w-full" />
          </Form.Item>
          <Form.Item
            label="输出价 ($/M tokens)"
            name="output_usd_per_million_tokens"
          >
            <InputNumber min={0} step={0.001} className="w-full" />
          </Form.Item>
          <Form.Item
            label="Cache读 ($/M)"
            name="cache_read_usd_per_million_tokens"
          >
            <InputNumber min={0} step={0.001} className="w-full" />
          </Form.Item>
          <Form.Item
            label="Cache写 ($/M)"
            name="cache_write_usd_per_million_tokens"
          >
            <InputNumber min={0} step={0.001} className="w-full" />
          </Form.Item>
          <Form.Item
            label="图片价 ($/M，一期只读)"
            name="image_usd_per_million_tokens"
          >
            <InputNumber disabled min={0} step={0.001} className="w-full" />
          </Form.Item>
          <Form.Item
            label="音频价 ($/M，一期只读)"
            name="audio_usd_per_million_tokens"
          >
            <InputNumber disabled min={0} step={0.001} className="w-full" />
          </Form.Item>
          <Form.Item label="固定价 ($，一期只读)" name="fixed_price_usd">
            <InputNumber disabled min={0} step={0.001} className="w-full" />
          </Form.Item>
          <Form.Item
            label="计费倍率 (默认 1.0)"
            name="price_multiplier"
            rules={[{ type: 'number', min: 0.001, message: '倍率必须大于 0' }]}
          >
            <InputNumber min={0.001} step={0.1} className="w-full" />
          </Form.Item>
          <Form.Item label="价格匹配优先级" name="priority">
            <InputNumber className="w-full" />
          </Form.Item>
        </div>
        <Form.Item label="免费规则" name="free" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
